import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
  ResponsiveModalClose,
  ResponsiveModalBody,
  ResponsiveModalFooter,
} from './ui/responsive-modal';

const DeleteProjectModal = ({ isOpen, onClose, project, onConfirmDelete }) => {
  const { t } = useTranslation();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setDeleting(false);
      setError(null);
    }
  }, [isOpen]);

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await onConfirmDelete(project);
      onClose();
    } catch (err) {
      console.error('Error moving project to trash:', err);
      setError(err.response?.data?.detail || t('projects.delete_project_failed'));
      setDeleting(false);
    }
  };

  if (!project) return null;

  return (
    <ResponsiveModal open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <ResponsiveModalContent className="bg-popover border-border md:rounded-xl">
        <ResponsiveModalHeader className="px-6 border-border">
          <div className="w-10 h-10 rounded-full bg-accent/50 flex items-center justify-center shrink-0">
            <Trash2 className="w-5 h-5 text-foreground/80" />
          </div>
          <div className="flex-1 min-w-0">
            <ResponsiveModalTitle className="text-lg text-foreground">
              {t('projects.delete_project_title')}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription className="text-sm text-muted-foreground">
              {t('projects.delete_project_restore_hint')}
            </ResponsiveModalDescription>
          </div>
          <ResponsiveModalClose className="text-muted-foreground hover:bg-accent hover:text-foreground p-2" />
        </ResponsiveModalHeader>

        <ResponsiveModalBody className="p-6 space-y-4">
          {error && (
            <div className="p-4 bg-red-500/20 border border-red-500/40 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <div className="p-4 bg-accent/60 border border-border rounded-lg">
            <p className="text-sm text-foreground/80">
              <strong className="text-foreground">"{project.name}"</strong> {t('projects.delete_project_body')}
            </p>
          </div>
        </ResponsiveModalBody>

        <ResponsiveModalFooter className="px-6 py-4 gap-3 border-border bg-accent/60">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="border-border text-foreground/80"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="bg-accent text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {deleting ? t('projects.delete_project_moving') : t('projects.delete_project_move_button')}
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
};

export default DeleteProjectModal;
